"use client";

import React, { useState, useRef, useEffect } from 'react'; 
import { useRouter } from 'next/navigation';
import styles from './PromptSelector.module.css';
import { track } from '@vercel/analytics/react';
import { pinterest } from '@/lib/pinterest'; // Add this import


interface PromptSelectorProps {
  onGenerateStatus: (hasGenerated: boolean) => void;
  onSuggestionsUpdate: (suggestions: string[]) => void;
  externalHasGenerated?: boolean;
}

const PromptSelector: React.FC<PromptSelectorProps> = ({
  onGenerateStatus,
  onSuggestionsUpdate,
  externalHasGenerated
}) => {
  const router = useRouter();
  
  // State management
  const [userInput, setUserInput] = useState<string>('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchCount, setSearchCount] = useState(0);

  const storageKeys = {
    suggestions: 'promptSelector_suggestions',
    input: 'promptSelector_input',
    hasGenerated: 'promptSelector_hasGenerated'
  };


 

  // Load saved state on component mount (client-side only)
useEffect(() => {
  if (typeof window === 'undefined') return;
  const savedSuggestions = sessionStorage.getItem(storageKeys.suggestions);
  const savedInput = sessionStorage.getItem(storageKeys.input);
  const savedHasGenerated = sessionStorage.getItem(storageKeys.hasGenerated);
  const savedSearchCount = sessionStorage.getItem('zilu_total_searches');
  if (savedSearchCount) {
    setSearchCount(parseInt(savedSearchCount, 10));
  }
  if (savedSuggestions) {
    try {
      const parsedSuggestions = JSON.parse(savedSuggestions);
      setSuggestions(parsedSuggestions);
      onSuggestionsUpdate(parsedSuggestions);
    } catch (e) {
      console.error('Error parsing saved suggestions:', e);
    }
  }

  if (savedInput) {
    setUserInput(savedInput);
  }

  if (savedHasGenerated === 'true') {
    onGenerateStatus(true);
  }
}, []);

// Save state whenever it changes (client-side only)
useEffect(() => {
  if (typeof window === 'undefined') return;
  if (suggestions.length > 0) {
    sessionStorage.setItem(storageKeys.suggestions, JSON.stringify(suggestions));
  }
}, [suggestions]);

useEffect(() => {
  if (typeof window === 'undefined') return;
  if (userInput) {
    sessionStorage.setItem(storageKeys.input, userInput);
  }
}, [userInput]);
  
  // Function to fetch suggestions from our API route
  const fetchSuggestions = async (query: string): Promise<string[]> => {
    setIsLoading(true);
    track("generated suggestions")
    const fullPrompt = `books like ${query}`;
    pinterest.trackSearch(fullPrompt); // No user data needed
    
    // Increment search count
    const newCount = searchCount + 1;
    setSearchCount(newCount);
    sessionStorage.setItem('zilu_total_searches', newCount.toString());
    
    // Track quality users at milestones
    if (newCount === 3 || newCount === 10) {
      pinterest.trackQualityUser(newCount);
    }
    try {
      const fullPrompt = `books like ${query}`;
      console.log("Sending prompt:", fullPrompt);
      
      const response = await fetch('/api/suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: fullPrompt }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch suggestions');
      }
      
      const data = await response.json();
      
      setIsLoading(false);
      return data.suggestions || [];
    } catch (error) {
      setIsLoading(false);
      // Return fallback suggestions in case of error
      return [
        `books like ${query} with unexpected twists`,
        `books like ${query} that explore human connections`,
        `books like ${query} with immersive worldbuilding`
      ];
    }
  };
  
  const handleGenerateSuggestions = async (): Promise<void> => {
    if (!userInput.trim()) return;
    
    const results = await fetchSuggestions(userInput);
    setSuggestions(results);

    sessionStorage.setItem(storageKeys.hasGenerated, 'true');

    onSuggestionsUpdate(results);
    onGenerateStatus(true);
  };  

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Close keyboard with slight delay for Android compatibility

      setTimeout(() => {
        inputRef.current?.blur();
      }, 100);
      
      handleGenerateSuggestions();
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleGenerateSuggestions();
  };
  
  return (
    <div className={styles.container}>
   <form onSubmit={handleFormSubmit} className={styles.searchContainer}>
        <div className={styles.inputContainer}>
          <span className={styles.fixedLabel}>Books like...</span>
          <input
            ref={inputRef}  
            type="text"
            value={userInput}
            onChange={(e) => {
              setUserInput(e.target.value);
              if (!e.target.value.trim()) {
                setSuggestions([]);
                onSuggestionsUpdate([]);
                onGenerateStatus(false);
                sessionStorage.removeItem(storageKeys.suggestions);
                sessionStorage.removeItem(storageKeys.input);
                sessionStorage.removeItem(storageKeys.hasGenerated);
              }
            }}
            className={styles.searchInput}
            onKeyDown={handleKeyDown}
            autoComplete="off"
            enterKeyHint="search"
          />
        </div>
        
        <button
          type="submit"
          disabled={isLoading || !userInput.trim()}
          className={isLoading || !userInput.trim() ? styles.generateButton : styles.generateButton}
        >
          {isLoading ? 'Loading' : 'Generate'}
        </button>
      </form>
    </div>
  );
};

export default PromptSelector;